import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { urlBase64ToUint8Array } from "../lib/push-utils";
import { useToast } from "./Toast";

const DISMISSED_KEY = "notif_prompt_dismissed";

export function NotificationBanner() {
  const { showToast } = useToast();
  const [show, setShow] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { data: vapidData } = useQuery({
    queryKey: ["vapid-key"],
    queryFn: () => api.get<{ publicKey: string }>("/api/push/vapid-key"),
    enabled: show === true,
  });

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISSED_KEY) === "1") {
        setShow(false);
        return;
      }
    } catch {
      setShow(false);
      return;
    }
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setShow(false);
      return;
    }
    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then((sub) => {
        setShow(!sub);
      });
    });
  }, []);

  const handleEnable = async () => {
    if (!vapidData?.publicKey) return;
    setIsLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        handleDismiss();
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidData.publicKey),
      });
      const json = sub.toJSON();
      await api.post("/api/push/subscribe", {
        endpoint: json.endpoint,
        p256dh: json.keys?.p256dh,
        auth: json.keys?.auth,
      });
      setShow(false);
      showToast("Notifiche abilitate", "success");
    } catch {
      showToast("Errore nell'abilitazione delle notifiche");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, "1");
    } catch {
      /* storage pieno */
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="bg-blue-50 border-b border-blue-100 px-4 py-3 flex items-center gap-3">
      <svg
        className="w-5 h-5 text-blue-500 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        viewBox="0 0 24 24"
      >
        <path d="M15 17H20L18.6 15.6A1 1 0 0118 14.8V11a6 6 0 00-5-5.9V4a1 1 0 00-2 0v1.1A6 6 0 006 11v3.8a1 1 0 01-.6.8L4 17h5m6 0v1a3 3 0 01-6 0v-1m6 0H9" />
      </svg>
      <p className="text-sm text-blue-800 flex-1">Vuoi ricevere notifiche sui promemoria di WorkIn?</p>
      <button
        onClick={handleEnable}
        disabled={isLoading || !vapidData}
        className="text-sm font-semibold text-blue-700 hover:text-blue-900 disabled:opacity-50 whitespace-nowrap transition-colors"
      >
        {isLoading ? "..." : "Attiva"}
      </button>
      <button
        onClick={handleDismiss}
        className="text-sm text-blue-400 hover:text-blue-600 whitespace-nowrap transition-colors"
      >
        Non ora
      </button>
    </div>
  );
}
